'use client';
import React, { useState, useEffect } from 'react';


const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Publishing', href: '#publishing' },
    { label: 'Contact', href: '#contact' },
  ];

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-nav py-2'
          : 'bg-white/95 backdrop-blur-sm py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => handleNavClick('#home')}
            className="flex items-center gap-3 group"
            aria-label="Caps N Corks II, LLC - Home"
          >
            <div className="w-10 h-10 bg-navy-DEFAULT rounded-lg flex items-center justify-center flex-shrink-0">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h16v2H4V4zm0 4h16v2H4V8zm0 4h10v2H4v-2zm0 4h16v2H4v-2z" fill="white"/>
                <circle cx="18" cy="14" r="4" fill="#387EF7"/>
                <path d="M17 13l1 1 2-2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="font-roboto font-700 text-navy-DEFAULT text-base leading-tight" style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 700, color: '#122658' }}>
                Caps N Corks II
              </span>
              <span className="text-xs text-body" style={{ fontFamily: 'Poppins, sans-serif', color: '#7F7F90', fontSize: '10px', letterSpacing: '1px' }}>
                LLC
              </span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-sm font-medium uppercase tracking-widest transition-colors duration-200 hover:text-primary-DEFAULT"
                style={{ fontFamily: 'Poppins, sans-serif', color: '#122658', letterSpacing: '1.5px', fontSize: '13px' }}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={() => handleNavClick('#contact')}
              className="inline-flex items-center justify-center px-6 py-3 text-white font-medium uppercase tracking-widest transition-all duration-200 hover:bg-primary-dark hover:shadow-lg"
              style={{
                backgroundColor: '#0066CC',
                borderRadius: '7px',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '13px',
                letterSpacing: '2px',
              }}
            >
              Request a Callback
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <div className="w-6 h-0.5 bg-navy-DEFAULT mb-1.5 transition-all" style={{ backgroundColor: '#122658' }} />
            <div className="w-6 h-0.5 bg-navy-DEFAULT mb-1.5 transition-all" style={{ backgroundColor: '#122658' }} />
            <div className="w-6 h-0.5 bg-navy-DEFAULT transition-all" style={{ backgroundColor: '#122658' }} />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-100">
            <nav className="flex flex-col gap-4 pt-4" aria-label="Mobile navigation">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className="text-left text-sm font-medium uppercase tracking-widest py-2 transition-colors duration-200 hover:text-primary-DEFAULT"
                  style={{ fontFamily: 'Poppins, sans-serif', color: '#122658', letterSpacing: '1.5px' }}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => handleNavClick('#contact')}
                className="inline-flex items-center justify-center px-6 py-3 text-white font-medium uppercase tracking-widest mt-2"
                style={{
                  backgroundColor: '#0066CC',
                  borderRadius: '7px',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                  letterSpacing: '2px',
                }}
              >
                Request a Callback
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
